import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { DataSourceService, DataSourceConfigOptions } from 'src/app/services/data-source.service';
import { PencilButtonComponent } from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { Observable } from 'rxjs';
import {
  SelectionListModalComponent,
  SelectionListModalSections,
} from '../selection-list-modal/selection-list-modal.component';
import { FilterData } from '../../../generic-filters-view/filters.interface';

export type MultiSelectionListModalSections<T> = SelectionListModalSections<T> & {
  selectedLabel?: TemplateRef<{ $implicit: number }>;
};

/**
 * A generic selection dialog component for selecting entities with search capabilities.
 * The entities are displayed in a **list** manner, and the user can **select multiple entities** from the list.
 * Supports customizable content sections with the input `sections`.
 *
 * If single selection in a list manner is needed, please use `single-selection-list-modal` instead.
 *
 * @template T The type of entity being displayed/selected in the dialog
 */
@Component({
  selector: 'app-multi-selection-list-modal',
  templateUrl: './multi-selection-list-modal.component.html',
  styleUrls: ['./multi-selection-list-modal.component.scss'],
  standalone: true,
  providers: [DataSourceService],
  imports: [
    CommonModule,
    MatCheckboxModule,
    TranslateModule,
    PencilButtonComponent,
    SelectionListModalComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectionListModalComponent<T extends Record<string, any>> implements OnInit {
  /**
   * Unique identifier for the dialog. Also used as a prefix for all
   * eligible elements for the `data-name` attribute.
   *
   * As an example, if the `id` value passed was `available-event-hosts-dialog`, the
   * entity checkbox's `data-name` attribute will be `available-event-hosts-dialog-select-item`.
   *
   * @required
   */
  @Input() id!: string;

  /**
   * The title displayed at the top of the dialog.
   *
   * @required
   */
  @Input() title!: string;

  /**
   * Placeholder text shown in the search input field.
   *
   * @required
   */
  @Input() searchPlaceholder!: string;
  /**
   * Configuration object that defines the customizable sections of the generic
   * entity selection dialog by using ng-template elements.
   * Currently, the supported configurable sections are `columns`, `contentHeader`, `footer` and `selectedLabel`.
   *
   * `columns` is **required**. This is where you define the columns in a
   * `{ [key]: Column }` manner to be displayed in the entity list. The possible
   * properties for the `Column` object are the following:
   * - `size`: number | 'fill' | 'fit' - The size of the column, either as a percentage of the total width (between 0 and 1),
   * or as `fill` to fill the remaining space, or as `fit` to only take the necessary space to fit the content.
   * Defaults to `fill` if not provided.
   * - `header`: TemplateRef<void> - The `ng-template` to display as the header of the column.
   * - `cell`: TemplateRef<{ $implicit: T }> - The `ng-template` to display as the cell of the column,
   * for each item of the entity list. Has access to the current item of the entity list
   * by using `let-item` or similar as context variable.
   *
   * `contentHeader` is **optional**. It's the header of the content section and serves to display
   * an important information above the entity list, which could be a title for the list,
   * a message regarding the entries being displayed, the current event's time, etc.
   * If not provided, nothing will be displayed above the entity list.
   *
   * `disclaimer` is **optional**. It's the disclaimer of the dialog, which can be used
   * to display a disclaimer above the entity list, e.g. to display a disclaimer
   * regarding the entries being displayed.
   * If not provided, no disclaimer will be displayed.
   *
   * `footer` is **optional**. It's the footer of the dialog, which can be used
   * to customize the dialog's bottom section actions, e.g. to display a button
   * to close the dialog or to create a new entity.
   * If not provided, no footer will be displayed.
   *
   * `selectedLabel` is **optional**. It's the label to display the current selected entities count
   * (at the footer, beside the `Done` button). The current count can be accessed by using `let-count`
   * as context variable.
   * If not provided, the label will be `{{count}} selected` by default.
   *
   * @required
   *
   * @example
   * ```html
   * <app-multi-selection-list-modal
   *   ...
   *   [sections]="{
   *     contentHeader,
   *     footer,
   *     selectedLabel,
   *     columns: {
   *       name: {
   *         flex: 2,
   *         header: nameHeader,
   *         cell: nameCell,
   *       },
   *       description: {
   *         header: descriptionHeader,
   *         cell: descriptionCell,
   *       },
   *     },
   *   }"
   * >
   *   <ng-template #contentHeader>
   *     <h3>Select an entity</h3>
   *   </ng-template>
   *
   *   <ng-template #nameHeader>Name</ng-template>
   *   <ng-template #nameCell let-item>{{ item.name }}</ng-template>
   *
   *   <ng-template #descriptionHeader>Description</ng-template>
   *   <ng-template #descriptionCell let-item>
   *     <!-- You can use any HTML element to display the item cell content. -->
   *     <div class="description-cell">
   *       <span>{{ item.description }}</span>
   *     </div>
   *   </ng-template>
   *
   *   <ng-template #footer>
   *     <button mat-raised-button color="primary" (click)="closeDialog()">Close</button>
   *   </ng-template>
   *
   *   <ng-template #selectedLabel let-count>
   *     <span>{{ count }} {{ count > 1 ? 'entries' : 'entry' }} selected</span>
   *   </ng-template>
   * </app-multi-selection-list-modal>
   * ```
   */
  @Input() sections!: MultiSelectionListModalSections<T>;

  /**
   * Defines configuration for how the data will be fetched and handled.
   *
   * @interface DataSourceConfigOptions<T>
   *
   * @property {FetchStrategy} fetchStrategy - The strategy to use for fetching the data, which
   * can be only `server-side` for now.
   * @property {Function} fetch - The function to use for fetching the data. Receives a `FetchDataParams` object
   * as a parameter, which contains the current @see{@link IFilterAPI} parameter, page index and page size;
   * and should return an Observable that emits a `FetchDataResult<T>` object, which contains the items and the total items.
   * This function will mainly be called when the dialog is opened (`ngAfterViewInit`) and whenever the user updates
   * the search query (by clicking on the search button or pressing enter in the search input field).
   * @property {PaginationStrategy} paginationStrategy - The strategy to use for pagination
   * (e.g. `scroll-based` or `page-based`).
   * @property {number} pageSize - The number of items to display per page.
   *
   * @required
   *
   * @example
   * At the component class, you can define the `fetch` function as follows:
   *
   * ```typescript
   * FetchStrategy = FetchStrategy;
   * PaginationStrategy = PaginationStrategy;
   *
   * constructor(...) {
   *   this.fetch = this.fetch.bind(this);
   * }
   *
   * fetch(params: DataSourceFetchParams): Observable<DataSourceFetchResult<T>> {
   *   return this.apiService.getEntities(params).pipe(
   *     map((res) => ({
   *       items: res.items,
   *       totalItems: res.totalItems,
   *     })),
   *   );
   * }
   * ```
   *
   * And at the template, you can pass the `fetch` function as a parameter:
   *
   * ```html
   * <app-multi-selection-list-modal
   *   ...
   *   [fetchConfig]="{
   *     fetchStrategy: FetchStrategy.ServerSide,
   *     fetch: fetch,
   *     paginationStrategy: PaginationStrategy.ScrollBased,
   *     pageSize: 10
   *   }"
   * >
   * ```
   */
  @Input() fetchConfig!: DataSourceConfigOptions<T>;

  /**
   * Observable that emits the filters to be displayed.
   *
   * Can be easily provided by using `FiltersFactoryService`, e.g.
   * `FiltersFactoryService.createProfilesFilters()`.
   */
  @Input() filters?: Observable<FilterData[]>;

  /**
   * Message to display when no entities are found.
   *
   * If not provided, the default message will be "No data found".
   */
  @Input() emptyMessage?: string;

  /**
   * Text to be displayed on the "Done" button.
   *
   * If not provided, the default text "Done" will be used.
   */
  @Input() doneButtonText?: string;

  /**
   * Text to be displayed on the "Cancel" button.
   *
   * If not provided, the default text "Cancel" will be used.
   */
  @Input() cancelButtonText?: string;

  /**
   * The property of the entity that will be used for comparison between entities.
   * This is needed for the `SelectionModel` to work properly and know how to compare entities.
   *
   * If not provided, the default property will be `_id`.
   */
  @Input() comparisonProperty: keyof T = '_id';

  /**
   * Event emitter that triggers when the "Done" button is clicked.
   * Emits the selected entities.
   *
   * If not subscribed to, the default behavior is that the dialog will close
   * when the "Done" button is clicked, with the currently selected entities being returned.
   */
  @Output() confirm = new EventEmitter<T[]>();

  /**
   * Event emitter that triggers when the dialog is cancelled, either by closing it
   * or clicking the "Cancel" button. Emits the currently selected entities when the dialog is cancelled,
   * which can be used to customize the cancellation logic (e.g. to display a confirmation dialog to the user).
   *
   * If not subscribed to, the default behavior is that the dialog will close
   * normally when the close button or the "Cancel" button is clicked, with no data being returned.
   */
  @Output() cancel = new EventEmitter<T[]>();

  @ViewChild('actionsHeader', { static: true }) actionsHeader!: TemplateRef<void>;
  @ViewChild('actionsCell', { static: true }) actionsCell!: TemplateRef<{ $implicit: T }>;

  columns: SelectionListModalSections<T>['columns'] = {};
  selection = new SelectionModel<T>(true, []);

  constructor(private dialogRef: MatDialogRef<MultiSelectionListModalComponent<T>>) {}

  ngOnInit(): void {
    // Aside from the columns received from the sections input, we also need to add
    // a new column with a checkbox to make the multi selection happen.
    this.columns = {
      ...this.sections.columns,
      actions: {
        size: 'fit',
        header: this.actionsHeader,
        cell: this.actionsCell,
      },
    };

    this.selection.compareWith = (a, b) =>
      a[this.comparisonProperty] === b[this.comparisonProperty];
  }

  onChange(item: T): void {
    if (this.selection.isSelected(item)) {
      // Deselection needs to be handled manually because of a bug in Angular's SelectionModel
      // (it's fixed on newer versions).
      const actualItem = this.selection.selected.find((x) =>
        this.selection.compareWith ? this.selection.compareWith(x, item) : x === item,
      );
      if (actualItem) {
        this.selection.deselect(actualItem);
      }
    } else {
      this.selection.select(item);
    }
  }

  onConfirm(): void {
    this.confirm.observed
      ? this.confirm.emit(this.selection.selected)
      : this.dialogRef.close(this.selection.selected);
  }

  onCancel(): void {
    this.cancel.observed ? this.cancel.emit(this.selection.selected) : this.dialogRef.close();
  }
}
